import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMessagesByConversationId } from "../store/chats/thunks";
import { selectChatsState } from "../store/chats/selectors";
import { scrollToBottom } from "../utils/uiElementScroll";

export const useInfiniteMessages = ({
  pageSize = 10,
  threshold = 0.8, // Load when 80% scrolled
  enabled = true,
} = {}) => {
  const dispatch = useDispatch();
  const { chats, selectedChatId } = useSelector(selectChatsState);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const scrollContainerRef = useRef(null);
  const previousChatIdRef = useRef(null);

  const selectedChat = useMemo(() => {
    return chats?.[selectedChatId];
  }, [selectedChatId, chats]);

  const messages = useMemo(() => {
    return selectedChat?.messages || [];
  }, [selectedChat?.messages]);

  const hasMore = useMemo(() => {
    return !selectedChat?.fetchedAll;
  }, [selectedChat?.fetchedAll]);

  const isLoading = useMemo(() => {
    return selectedChat?.loading || isFetchingMore;
  }, [selectedChat?.loading, isFetchingMore]);

  // Reset on chat change
  useEffect(() => {
    if (previousChatIdRef.current !== selectedChatId) {
      setIsFetchingMore(false);
      previousChatIdRef.current = selectedChatId;
    }
  }, [selectedChatId]);

  // Fetch function with debouncing
  const fetchMessages = useCallback(
    async (offset = 0, isInitial = false) => {
      if (
        !selectedChat?.id ||
        (!isInitial && (isLoading || !hasMore || !enabled))
      )
        return;

      setIsFetchingMore(true);

      try {
        await dispatch(
          fetchMessagesByConversationId({
            conversation_id: selectedChat.id,
            limit: pageSize,
            offset,
          }),
        ).unwrap();
      } catch (error) {
        console.error("Failed to fetch messages:", error);
      } finally {
        setIsFetchingMore(false);
      }
    },
    [dispatch, selectedChat?.id, isLoading, hasMore, pageSize, enabled],
  );

  useEffect(() => {
    if (!scrollContainerRef.current) return;

    scrollToBottom(scrollContainerRef);
  }, [selectedChatId]);

  // Load more function
  const loadMore = useCallback(async () => {
    if (!isLoading && hasMore && enabled) {
      const prevHeight = scrollContainerRef.current.scrollHeight;

      await fetchMessages(messages.length);

      requestAnimationFrame(() => {
        const newHeight = scrollContainerRef.current.scrollHeight;
        scrollContainerRef.current.scrollTop = newHeight - prevHeight;
      });
    }
  }, [fetchMessages, messages.length, isLoading, hasMore, enabled]);

  // Scroll handler for container-based infinite scroll
  const handleScroll = useCallback(
    (e) => {
      if (!enabled || isLoading || !hasMore) return;

      const container = e.target;
      const { scrollTop, scrollHeight, clientHeight } = container;

      // For chat: load when scrolling UP near the top (older messages)
      const scrollPercentage =
        Math.abs(scrollTop) / (scrollHeight - clientHeight);

      if (scrollPercentage >= threshold) {
        loadMore();
      }
    },
    [enabled, isLoading, hasMore, threshold, loadMore],
  );

  // Intersection Observer for element-based trigger
  const observerTarget = useCallback(
    (node) => {
      if (!enabled || isLoading || !hasMore) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMore();
          }
        },
        { threshold: 0.1, rootMargin: "100px" },
      );

      if (node) observer.observe(node);

      return () => observer.disconnect();
    },
    [enabled, isLoading, hasMore, loadMore],
  );

  // Refresh function
  const refresh = useCallback(async () => {
    await fetchMessages(0, true);
  }, [fetchMessages]);

  const onSendMessage = useCallback(() => {
    scrollToBottom(scrollContainerRef);
  }, []);

  return {
    // Data
    messages,
    selectedChat,
    selectedChatId,

    // State
    isLoading,
    isFetchingMore,
    hasMore,

    // Actions
    loadMore,
    refresh,

    // Refs for different scroll strategies
    scrollContainerRef,
    observerTarget,
    handleScroll,
    onSendMessage,
  };
};
